// Error Reporting System for VRP Unified Architecture
import { VRPError, ErrorReportingConfig } from '../types/errors';

class GlobalErrorReporting {
  private config: ErrorReportingConfig = {
    enabled: process.env.NODE_ENV === 'production',
    enableConsoleLogging: process.env.NODE_ENV === 'development',
    enableLocalStorage: true,
    maxStoredErrors: 50,
    sampleRate: 1.0
  };

  private errorQueue: VRPError[] = [];
  private userId: string | null = null;
  private sessionId: string | null = null;

  configure(config: Partial<ErrorReportingConfig>): void {
    this.config = { ...this.config, ...config };
    console.log('[ErrorReporting] Configured:', this.config);
  }

  setUserId(userId: string): void {
    this.userId = userId;
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }

  reportError(error: VRPError): void {
    if (!this.config.enabled) {
      return;
    }

    // Apply sampling rate
    if (Math.random() > this.config.sampleRate) {
      return;
    }

    // Enhance error with user context
    const enhancedError = this.enhanceError(error);

    // Console logging for development
    if (this.config.enableConsoleLogging) {
      this.logToConsole(enhancedError);
    }

    // Store locally for debugging
    if (this.config.enableLocalStorage) {
      this.storeLocally(enhancedError);
    }

    // Queue for external reporting
    this.queueForReporting(enhancedError);

    // Send to external service if configured
    if (this.config.endpoint) {
      this.sendToExternalService(enhancedError);
    }
  }

  reportCustomError(
    message: string,
    category: string,
    metadata?: Record<string, any>
  ): void {
    const customError: VRPError = {
      id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      category: category as any,
      severity: 'MEDIUM' as any,
      message,
      context: {
        route: window.location.pathname,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        sessionId: this.sessionId || 'unknown',
        userId: this.userId || undefined
      },
      metadata,
      recovered: false
    };

    this.reportError(customError);
  }

  getStoredErrors(): VRPError[] {
    if (!this.config.enableLocalStorage) {
      return [];
    }

    try {
      const stored = localStorage.getItem('vrp_stored_errors');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  clearStoredErrors(): void {
    localStorage.removeItem('vrp_stored_errors');
    this.errorQueue = [];
  }

  getErrorStats(): {
    total: number;
    bySeverity: Record<string, number>;
    byCategory: Record<string, number>;
    recentErrors: VRPError[];
  } {
    const stored = this.getStoredErrors();
    
    const bySeverity: Record<string, number> = {};
    const byCategory: Record<string, number> = {};

    stored.forEach(error => {
      bySeverity[error.severity] = (bySeverity[error.severity] || 0) + 1;
      byCategory[error.category] = (byCategory[error.category] || 0) + 1;
    });

    return {
      total: stored.length,
      bySeverity,
      byCategory,
      recentErrors: stored.slice(-10)
    };
  }

  private enhanceError(error: VRPError): VRPError {
    return {
      ...error,
      context: {
        ...error.context,
        userId: this.userId || error.context.userId,
        sessionId: this.sessionId || error.context.sessionId,
        timestamp: error.context.timestamp || Date.now(),
        userAgent: error.context.userAgent || navigator.userAgent,
        route: error.context.route || window.location.pathname
      }
    };
  }

  private logToConsole(error: VRPError): void {
    const logLevel = this.getLogLevel(error.severity);
    const logMethod = console[logLevel] || console.error;

    logMethod(
      `[VRP Error] ${error.category} - ${error.severity}:`,
      error.message,
      {
        id: error.id,
        context: error.context,
        metadata: error.metadata,
        originalError: error.originalError
      }
    );
  }

  private getLogLevel(severity: string): 'error' | 'warn' | 'info' {
    switch (severity) {
      case 'CRITICAL':
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warn';
      case 'LOW':
        return 'info';
      default:
        return 'error';
    }
  }

  private storeLocally(error: VRPError): void {
    try {
      const stored = this.getStoredErrors();
      stored.push(error);

      // Keep only the most recent errors
      const maxErrors = this.config.maxStoredErrors || 50;
      if (stored.length > maxErrors) {
        stored.splice(0, stored.length - maxErrors);
      }

      localStorage.setItem('vrp_stored_errors', JSON.stringify(stored));
    } catch (e) {
      console.warn('[ErrorReporting] Failed to store error locally:', e);
    }
  }

  private queueForReporting(error: VRPError): void {
    this.errorQueue.push(error);

    // Process queue periodically
    if (this.errorQueue.length === 1) {
      setTimeout(() => this.processQueue(), 5000);
    }
  }

  private async processQueue(): Promise<void> {
    if (this.errorQueue.length === 0) {
      return;
    }

    const errorsToSend = [...this.errorQueue];
    this.errorQueue = [];

    if (this.config.endpoint) {
      try {
        await this.sendBatchToExternalService(errorsToSend);
      } catch (e) {
        console.warn('[ErrorReporting] Failed to send error batch:', e);
        // Re-queue errors on failure
        this.errorQueue.unshift(...errorsToSend);
      }
    }
  }

  private async sendToExternalService(error: VRPError): Promise<void> {
    if (!this.config.endpoint) {
      return;
    }

    try {
      const response = await fetch(this.config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.config.apiKey && { 'Authorization': `Bearer ${this.config.apiKey}` })
        },
        body: JSON.stringify({
          errors: [error],
          timestamp: Date.now(),
          source: 'vrp-frontend'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (e) {
      console.warn('[ErrorReporting] Failed to send error to external service:', e);
    }
  }

  private async sendBatchToExternalService(errors: VRPError[]): Promise<void> {
    if (!this.config.endpoint || errors.length === 0) {
      return;
    }

    try {
      const response = await fetch(this.config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.config.apiKey && { 'Authorization': `Bearer ${this.config.apiKey}` })
        },
        body: JSON.stringify({
          errors,
          timestamp: Date.now(),
          source: 'vrp-frontend',
          batch: true
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log(`[ErrorReporting] Successfully sent batch of ${errors.length} errors`);
    } catch (e) {
      console.warn('[ErrorReporting] Failed to send error batch to external service:', e);
      throw e;
    }
  }
}

// Global instance
export const globalErrorReporting = new GlobalErrorReporting();

// Auto-configure based on environment
if (process.env.NODE_ENV === 'production') {
  globalErrorReporting.configure({
    enabled: true,
    enableConsoleLogging: false,
    sampleRate: 1.0
  });
} else {
  globalErrorReporting.configure({
    enabled: true,
    enableConsoleLogging: true,
    sampleRate: 1.0
  });
}